import { useContext, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-dsgov';
import { BrButton, BrDivider, BrInput, BrLoading, BrSelect } from '@govbr-ds/react-components';
import style from './formNCMReceitaBrutaCalculada.module.scss';
import AtualizarInformacoes from '../../../../../../../../atualizarInformacoes/atualizarInformacoes';
import AnmSwitch from '../../../../../../../../../../../components/anmSwitch/anmSwitch';
import { validarReceitaBrutaCalculada } from './formNCMReceitaBrutaCalculadaValidacoes';
import { useNotificacao } from '../../../../../../../../../../../hooks/useNotificacao';
import { EMetodoReceitaBrutaCalculada } from '../../../../../../../../../../../enums/EMetodoBaseCalculo';
import { useBackEndRequests } from '../../../../../../../../../lib/backEndRequests';
import { DiefCfemContext } from '../../../../../../../../../../../contexts/DiefCfemContext';
import AnmSelectQuery from '../../../../../../../../../../../components/anmSelectQuery/anmSelectQuery';
import {
  IFormReceitaBrutaCalculada,
  INCMReceitaBrutaCalculadaPrecoCorrente,
  INCMReceitaBrutaCalculadaValorReferencia,
  IReceitaBrutaCalculada,
  IRequisicaoReceitaBrutaCalculadaPrecoCorrente,
  IRequisicaoReceitaBrutaCalculadaValorReferencia,
} from '../../../../../../../../../../../interfaces/IReceitaBrutaCalculada';
import { formatCurrency, removeFormatCurrency } from '../../../../../../../../../../../utils/formatCurrency';
import { useQuery } from '@tanstack/react-query';
import { formatFloatToBR, formatFloatToUS } from '../../../../../../../../../../../utils/formatFloat';
import InformacaoBasica from '../../../../../../../../../../../components/informacaoBasica/informacaoBasica';
import InfoCircleTooltip from '../../../../../../../../../../../components/infoCircleTooltip/infoCircleTooltip';
import { onTextAreaChange } from '../../../../../../../../../../../utils/textAreaSize';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { ITributos } from 'interfaces/ITributos';
import SecaoDeducoes from 'components/secaoDeducoes/secaoDeducoes';
import { tratarMsgErroRequisicoesBack } from 'utils/tratarMsgErro';

interface IFormProps {
  refetchListaNCM: () => void;
  isEdicaoNcm: boolean;
  setIsEdicaoNcm?: React.Dispatch<React.SetStateAction<boolean>>;
}

const FormNCMReceitaBrutaCalculada = ({ refetchListaNCM, isEdicaoNcm, setIsEdicaoNcm }: IFormProps) => {
  const [tipoMetodo, setTipoMetodo] = useState<EMetodoReceitaBrutaCalculada>(
    EMetodoReceitaBrutaCalculada.PRECO_CORRENTE
  );

  const [tributosAdicionados, setTributosAdicionados] = useState<
    {
      nome: string;
      valor: number;
    }[]
  >([]);

  const [codigoNcmBemMineral, setCodigoNcmBemMineral] = useState<string>('');
  const [unidadeMedida, setUnidadeMedida] = useState<string>('');
  const [faseDeIndustrializacao, setFaseDeIndustrializacao] = useState<string>('');

  const [novaEspecie, setNovaEspecie] = useState<string>('');
  const [codigoNcmNovaEspecie, setCodigoNcmNovaEspecie] = useState<string>('');
  const [unidadeMedidaNovaEspecie, setUnidadeMedidaNovaEspecie] = useState<string>('');

  const [quantidadeConsumidaNovaEspecie, setQuantidadeConsumidaNovaEspecie] = useState<string>('');
  const [quantidadeUtilizadaDoadaOuBonificada, setQuantidadeUtilizadaDoadaOuBonificada] = useState<string>('');

  const [precoCorrente, setPrecoCorrente] = useState<string>('');
  const [valorProducaoTotal, setValorProducaoTotal] = useState<string>('');

  const [teorConcentrado, setTeorConcentrado] = useState<string>('');
  const [teorAlimentacao, setTeorAlimentacao] = useState<string>('');
  const [idSubstanciaFatorDeAjuste, setIdSubstanciaFatorDeAjuste] = useState<string>('');

  const [haCompraTerceiro, setHaCompraTerceiro] = useState<boolean>(false);
  const [quantidadeAquisicaoTerceiros, setQuantidadeAquisicaoTerceiros] = useState<string>('');
  const [valorTotalAquisicaoTerceiros, setValorTotalAquisicaoTerceiros] = useState<string>('');

  const [isRejeito, setIsRejeito] = useState<boolean>(false);
  const [hasUsoEspecifico, setHasUsoEspecifico] = useState<boolean>(false);

  const [isLoadingInfo, setIsLoadingInfo] = useState<boolean>(false);
  const [loadingBotao, setLoadingBotao] = useState<boolean>(false);
  const [info, setInfo] = useState<{ label: string; value: number }[]>([
    { label: 'Deduções (R$)', value: 0 },
    { label: 'Base de Cálculo (R$)', value: 0 },
  ]);

  const [hasFatorAjuste, setHasFatorAjuste] = useState<boolean>(false);

  const [isCodigoNCMNovaEspecieValid, setIsCodigoNCMNovaEspecieValid] = useState<boolean>(false);
  const [codigoNCMNovaEspecieComDescricao, setCodigoNCMNovaEspecieComDescricao] = useState<string>('');
  const [errorCodigoNCMNovaEspecie, setErrorCodigoNCMNovaEspecie] = useState<string>('');
  const [isCodigoNCMLoading, setIsCodigoNCMLoading] = useState<boolean>(false);

  const {
    getNCMReceitaBruta,
    getUnidadesMedidaReceitaBruta,
    getFatoresAjusteReceitaBrutaCalculada,
    postReceberInformacoesReceitaBrutaCalculadaPrecoCorrente,
    postReceberInformacoesReceitaBrutaCalculadaValorReferencia,
    postSalvarNCMReceitaBrutaCalculadaPrecoCorrente,
    putSalvarNCMReceitaBrutaCalculadaPrecoCorrente,
    postSalvarNCMReceitaBrutaCalculadaValorReferencia,
    putSalvarNCMReceitaBrutaCalculadaValorReferencia,
    getNCMAdicionadoReceitaBrutaCalculadaPrecoCorrente,
    getNCMAdicionadoReceitaBrutaCalculadaValorReferencia,
    getCodigoNCMNovaEspecie,
    getTributosReceitaBrutaCalculada,
  } = useBackEndRequests();

  const context = useContext(DiefCfemContext);
  const idOperacaoEditar = parseInt(context?.idOperacaoEditar ?? '-1');
  const idReceitaBrutaCalculada = context?.idReceitaBrutaCalculada ?? -1;
  const receitaBrutaCalculadaNCM = context?.idReceitaBrutaCalculadaNCM ?? -1;
  const receitaBrutaCalculadaMetodo =
    context?.receitaBrutaCalculadaMetodo ?? EMetodoReceitaBrutaCalculada.PRECO_CORRENTE;
  const numeroProcesso = context?.nrProcessoMinerarioEmPreenchimento || '';

  const { data: ncmAdicionadoValorReferenciaData, isLoading: isLoadingNcmAdicionadoValorReferencia } = useQuery<
    INCMReceitaBrutaCalculadaValorReferencia,
    IErrorWithSentryId
  >({
    queryKey: [
      'getNCMAdicionadoReceitaBrutaCalculadaValorReferencia',
      receitaBrutaCalculadaNCM,
      receitaBrutaCalculadaMetodo,
    ],
    queryFn: () => getNCMAdicionadoReceitaBrutaCalculadaValorReferencia(receitaBrutaCalculadaNCM),

    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled:
      isEdicaoNcm &&
      receitaBrutaCalculadaNCM !== -1 &&
      receitaBrutaCalculadaMetodo === EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA,
  });

  const { data: ncmAdicionadoPrecoCorrenteData, isLoading: isLoadingNcmAdicionadoPrecoCorrente } = useQuery<
    INCMReceitaBrutaCalculadaPrecoCorrente,
    IErrorWithSentryId
  >({
    queryKey: [
      'getNCMAdicionadoReceitaBrutaCalculadaPrecoCorrente',
      receitaBrutaCalculadaNCM,
      receitaBrutaCalculadaMetodo,
    ],
    queryFn: () => getNCMAdicionadoReceitaBrutaCalculadaPrecoCorrente(receitaBrutaCalculadaNCM),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled:
      isEdicaoNcm &&
      receitaBrutaCalculadaNCM !== -1 &&
      receitaBrutaCalculadaMetodo === EMetodoReceitaBrutaCalculada.PRECO_CORRENTE,
  });

  const {
    data: fatorDeAjusteData,
    isLoading: fatorDeAjusteDataLoading,
    isFetching: fatorDeAjusteDataFetching,
    isError: fatorDeAjusteError,
    refetch: refetchFatorDeAjuste,
  } = useQuery<any, IErrorWithSentryId>({
    queryKey: ['getFatoresAjusteReceitaBrutaCalculada', idOperacaoEditar],
    queryFn: () => getFatoresAjusteReceitaBrutaCalculada(idOperacaoEditar),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: !isEdicaoNcm && idOperacaoEditar !== -1,
  });

  const {
    data: tributosPossiveisData,
    isLoading: tributosPossiveisLoading,
    error: tributosPossiveisError,
    refetch: refetchTributosPossiveis,
  } = useQuery<ITributos, IErrorWithSentryId>({
    queryKey: ['getTributosReceitaBrutaCalculada', idOperacaoEditar],
    queryFn: () => getTributosReceitaBrutaCalculada(idOperacaoEditar.toString()),
    refetchOnWindowFocus: false,
  });
  const mostrarNotificacao = useNotificacao();

  useEffect(() => {
    const CALCULO_BASE = [
      { label: 'Receita Bruta Calculada (R$)', value: 0 },
      { label: 'Base de Cálculo (R$)', value: 0 },
    ];

    const CALCULO_VALOR_REFERENCIA = [
      { label: 'Índice de Enriquecimento', value: 0, format: (value: number) => formatarFloat(value) },
      { label: 'Fator de Ajuste', value: 0, format: (value: number) => formatarFloat(value) },
      { label: 'Valor de Referência Unitário (R$)', value: 0 },
      { label: 'Receita Bruta Calculada (R$)', value: 0 },
      { label: 'Base de Cálculo (R$)', value: 0 },
    ];

    if (tipoMetodo === EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA) {
      setInfo([...CALCULO_VALOR_REFERENCIA]);
    } else {
      setInfo([...CALCULO_BASE]);
    }
  }, [tipoMetodo]);

  useEffect(() => {
    const ncmAdicionadoData = ncmAdicionadoPrecoCorrenteData || ncmAdicionadoValorReferenciaData;

    if (isEdicaoNcm && ncmAdicionadoData) {
      setTipoMetodo(receitaBrutaCalculadaMetodo);
      setCodigoNcmBemMineral(ncmAdicionadoData.ncmBemMineral.codigo);
      setUnidadeMedida(ncmAdicionadoData.unidadeMedida);
      setFaseDeIndustrializacao(ncmAdicionadoData.faseDeIndustrializacao);
      setNovaEspecie(ncmAdicionadoData.novaEspecie);
      setCodigoNcmNovaEspecie(ncmAdicionadoData.ncmNovaEspecie.codigo);
      setUnidadeMedidaNovaEspecie(ncmAdicionadoData.unidadeMedidaNovaEspecie);
      setQuantidadeConsumidaNovaEspecie(formatFloatToBR(ncmAdicionadoData.quantidadeNovaEspecie.toString()));
      setQuantidadeUtilizadaDoadaOuBonificada(
        formatFloatToBR(ncmAdicionadoData.quantidadeUtilizadaDoadaOuBonificada.toString())
      );
      setIsCodigoNCMNovaEspecieValid(true);
      setCodigoNCMNovaEspecieComDescricao(
        ncmAdicionadoData.ncmNovaEspecie.codigo + ' - ' + ncmAdicionadoData.ncmNovaEspecie.descricao
      );

      if (ncmAdicionadoData.quantidadeAquisicaoTerceiros !== 0 && ncmAdicionadoData.valorAquisicaoTerceiros !== 0) {
        setHaCompraTerceiro(true);
        setQuantidadeAquisicaoTerceiros(ncmAdicionadoData.quantidadeAquisicaoTerceiros.toString());
        setValorTotalAquisicaoTerceiros(formatCurrency(ncmAdicionadoData.valorAquisicaoTerceiros));
      }

      setIsRejeito(ncmAdicionadoData.isRejeito);
      setHasUsoEspecifico(ncmAdicionadoData.hasUsoEspecifico);
      setTributosAdicionados(ncmAdicionadoData.tributosValores);

      if (receitaBrutaCalculadaMetodo === EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA) {
        const ncmValorReferencia = ncmAdicionadoData as INCMReceitaBrutaCalculadaValorReferencia;

        setValorProducaoTotal(formatCurrency(ncmValorReferencia.valorProducaoTotal));
        setTeorConcentrado(formatCurrency(ncmValorReferencia.teorConcentrado));
        setTeorAlimentacao(formatCurrency(ncmValorReferencia.teorAlimentacao));
      }

      if (receitaBrutaCalculadaMetodo === EMetodoReceitaBrutaCalculada.PRECO_CORRENTE) {
        const ncmPrecoCorrente = ncmAdicionadoData as INCMReceitaBrutaCalculadaPrecoCorrente;

        setPrecoCorrente(formatCurrency(ncmPrecoCorrente.precoCorrente));
      }
    }
  }, [isEdicaoNcm, ncmAdicionadoPrecoCorrenteData, ncmAdicionadoValorReferenciaData, receitaBrutaCalculadaMetodo]);

  useEffect(() => {
    if (isEdicaoNcm && ncmAdicionadoValorReferenciaData) {
      if (ncmAdicionadoValorReferenciaData.substanciaFatorAjuste) {
        setIdSubstanciaFatorDeAjuste(ncmAdicionadoValorReferenciaData.substanciaFatorAjuste.id.toString());
      } else {
        setIdSubstanciaFatorDeAjuste('-1');
      }
    }
  }, [isEdicaoNcm, ncmAdicionadoValorReferenciaData]);

  useEffect(() => {
    if (fatorDeAjusteData && !fatorDeAjusteError) {
      setHasFatorAjuste(fatorDeAjusteData.fatoresAjuste?.length > 0);
    }
  }, [fatorDeAjusteData, fatorDeAjusteError]);

  const formatarSubstanciaFatorAjuste = (fatorAjuste: string) => {
    if (fatorAjuste === '-1') {
      return null;
    } else {
      return parseInt(fatorAjuste);
    }
  };

  const formatarFloat = (value: number) => {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  };

  const limparFormulario = () => {
    setTipoMetodo(EMetodoReceitaBrutaCalculada.PRECO_CORRENTE);
    setCodigoNcmBemMineral('');
    setUnidadeMedida('');
    setFaseDeIndustrializacao('');
    setNovaEspecie('');
    setCodigoNcmNovaEspecie('');
    setUnidadeMedidaNovaEspecie('');
    setQuantidadeConsumidaNovaEspecie('');
    setQuantidadeUtilizadaDoadaOuBonificada('');
    setPrecoCorrente('');
    setValorProducaoTotal('');
    setTeorConcentrado('');
    setTeorAlimentacao('');
    setIdSubstanciaFatorDeAjuste('');
    setHaCompraTerceiro(false);
    setQuantidadeAquisicaoTerceiros('');
    setValorTotalAquisicaoTerceiros('');
    setIsRejeito(false);
    setHasUsoEspecifico(false);
    setInfo([
      { label: 'Deduções (R$)', value: 0 },
      { label: 'Base de Cálculo (R$)', value: 0 },
    ]);
    setCodigoNCMNovaEspecieComDescricao('');
    setIsCodigoNCMNovaEspecieValid(false);
    setErrorCodigoNCMNovaEspecie('');
    setTributosAdicionados([]);
  };

  const validarFormulario = () => {
    const formValues: IFormReceitaBrutaCalculada = {
      tipoMetodo: tipoMetodo,
      codigoNcmBemMineral: codigoNcmBemMineral,
      unidadeMedida: unidadeMedida,
      faseDeIndustrializacao: faseDeIndustrializacao,
      novaEspecie: novaEspecie,
      codigoNcmNovaEspecie: isCodigoNCMNovaEspecieValid ? codigoNcmNovaEspecie : undefined,
      unidadeMedidaNovaEspecie: unidadeMedidaNovaEspecie,
      quantidadeConsumidaNovaEspecie: quantidadeConsumidaNovaEspecie,
      quantidadeUtilizadaDoadaOuBonificada: quantidadeUtilizadaDoadaOuBonificada,
      precoCorrente: precoCorrente,
      valorProducaoTotal: valorProducaoTotal,
      haCompraTerceiro: haCompraTerceiro,
      isRejeito: isRejeito,
      hasUsoEspecifico: hasUsoEspecifico,
      teorConcentrado: teorConcentrado,
      teorAlimentacao: teorAlimentacao,
      idSubstanciaFatorDeAjuste: idSubstanciaFatorDeAjuste,
      quantidadeAquisicaoTerceiros: quantidadeAquisicaoTerceiros,
      valorTotalAquisicaoTerceiros: valorTotalAquisicaoTerceiros,
      tributosValores: tributosAdicionados,
      tributosPossiveis: tributosPossiveisData,
    };

    return validarReceitaBrutaCalculada(formValues);
  };

  const montarRequestBase = (): IReceitaBrutaCalculada => {
    const requestBase = {
      idReceitaBrutaCalculada,
      codigoNcmBemMineral,
      unidadeMedida,
      faseDeIndustrializacao,
      novaEspecie,
      codigoNcmNovaEspecie,
      unidadeMedidaNovaEspecie,
      quantidadeConsumidaNovaEspecie: parseFloat(formatFloatToUS(quantidadeConsumidaNovaEspecie || '0')),
      quantidadeUtilizadaDoadaOuBonificada: parseFloat(formatFloatToUS(quantidadeUtilizadaDoadaOuBonificada || '0')),
      quantidadeAquisicaoTerceiros: 0,
      valorTotalAquisicaoTerceiros: 0,
      isRejeito,
      hasUsoEspecifico,
      tributosValores: tributosAdicionados,
    };

    if (haCompraTerceiro) {
      requestBase.quantidadeAquisicaoTerceiros = parseFloat(quantidadeAquisicaoTerceiros) || 0;
      requestBase.valorTotalAquisicaoTerceiros = removeFormatCurrency(valorTotalAquisicaoTerceiros);
    }

    return requestBase;
  };

  const montarRequestPrecoCorrente = (): IRequisicaoReceitaBrutaCalculadaPrecoCorrente => {
    const requestBase = montarRequestBase();

    return {
      ...requestBase,

      precoCorrente: removeFormatCurrency(precoCorrente),
    };
  };

  const montarRequestValorReferencia = (): IRequisicaoReceitaBrutaCalculadaValorReferencia => {
    const requestBase = montarRequestBase();

    return {
      ...requestBase,

      valorProducaoTotal: removeFormatCurrency(valorProducaoTotal),
      teorConcentrado: removeFormatCurrency(teorConcentrado),
      teorAlimentacao: removeFormatCurrency(teorAlimentacao),
      idSubstanciaFatorDeAjuste: formatarSubstanciaFatorAjuste(idSubstanciaFatorDeAjuste),
    };
  };

  const receberInformacoesPrecoCorrente = () => {
    const requestBody = montarRequestPrecoCorrente();

    setIsLoadingInfo(true);

    postReceberInformacoesReceitaBrutaCalculadaPrecoCorrente(requestBody)
      .then((result) => {
        if (result.corrente) {
          const info = [
            { label: 'Receita Bruta Calculada (R$)', value: result.corrente?.receitaBrutaCalculada },
            ...(result.corrente?.deducoes > 0 ? [{ label: 'Deduções (R$)', value: result.corrente?.deducoes }] : []),
            { label: 'Base de Cálculo (R$)', value: result.corrente?.baseCalculo },
          ];

          setInfo(info);
        }
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: tratarMsgErroRequisicoesBack(
            err,
            'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.'
          ),
          ...(err && { complementoSentryId: err }),
        });
      })
      .finally(() => {
        setIsLoadingInfo(false);
      });
  };

  const receberInformacoesValorReferencia = async () => {
    const requestBody = montarRequestValorReferencia();

    setIsLoadingInfo(true);

    postReceberInformacoesReceitaBrutaCalculadaValorReferencia(requestBody)
      .then((result) => {
        if (result.referencia) {
          const info = [
            {
              label: 'Índice de Enriquecimento',
              value: result.referencia.indiceEnriquecimento,
              format: (value: number) => formatarFloat(value),
            },
            {
              label: 'Fator de Ajuste',
              value: result.referencia.fatorAjuste,
              format: (value: number) => formatarFloat(value),
            },
            { label: 'Valor de Referência Unitário (R$)', value: result.referencia.valorReferenciaUnitario },
            { label: 'Receita Bruta Calculada (R$)', value: result.referencia.receitaBrutaCalculada },
            ...(result.referencia?.deducoes > 0
              ? [{ label: 'Deduções (R$)', value: result.referencia?.deducoes }]
              : []),
            { label: 'Base de Cálculo (R$)', value: result.referencia.baseCalculo },
          ];

          setInfo(info);
        }
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: tratarMsgErroRequisicoesBack(
            err,
            'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.'
          ),
          ...(err && { complementoSentryId: err }),
        });
      })
      .finally(() => {
        setIsLoadingInfo(false);
      });
  };

  const receberInformacoesBaseCalculo = async () => {
    const msgErroValidacao = validarFormulario();
    if (msgErroValidacao != '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: msgErroValidacao,
      });

      return false;
    }

    if (tipoMetodo === EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA) {
      receberInformacoesValorReferencia();
    } else {
      receberInformacoesPrecoCorrente();
    }
  };

  const formatResultFatorAjuste = (result: any) => {
    if (result?.fatoresAjuste) {
      return result?.fatoresAjuste.map((fator: any) => ({
        label: fator.nomeSubstancia,
        value: fator.substanciaCorrelacionadaId,
      }));
    }
    return [];
  };

  const salvarNCM = () => {
    const msgErroValidacao = validarFormulario();

    if (msgErroValidacao === '') {
      executarRequestNCM();
    } else {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: msgErroValidacao,
      });
    }
  };

  const navegarParaListaNCM = async () => {
    const buttonEdit = document.getElementById('lista-ncm-adicionado');

    if (buttonEdit) {
      window.scrollTo({
        top: buttonEdit.offsetTop,
      });
    }
  };

  const executarRequestNCM = () => {
    let funcaoSalvar: Function = salvarPrecoCorrente;

    if (tipoMetodo === EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA) {
      funcaoSalvar = salvarValorReferencia;
    }

    setLoadingBotao(true);

    funcaoSalvar()
      .then(() => {
        limparFormulario();
        refetchListaNCM();
        navegarParaListaNCM();

        if (setIsEdicaoNcm && isEdicaoNcm) {
          setIsEdicaoNcm(false);
        }
      })
      .catch((err: IErrorWithSentryId) => {
        const textoAcao: string = isEdicaoNcm ? 'salvar' : 'enviar';

        mostrarNotificacao({
          tipo: 'danger',
          conteudo: tratarMsgErroRequisicoesBack(err, `Erro ao ${textoAcao} NCM, tente novamente.`),
        });
      })
      .finally((): void => {
        setLoadingBotao(false);
      });
  };

  const salvarPrecoCorrente = () => {
    const requestBody = montarRequestPrecoCorrente();

    if (isEdicaoNcm) {
      const requestComId = {
        ...requestBody,
        id: receitaBrutaCalculadaNCM,
      };
      return putSalvarNCMReceitaBrutaCalculadaPrecoCorrente(requestComId);
    } else {
      return postSalvarNCMReceitaBrutaCalculadaPrecoCorrente(requestBody);
    }
  };

  const salvarValorReferencia = () => {
    const requestBody = montarRequestValorReferencia();

    if (isEdicaoNcm) {
      const requestComId = {
        ...requestBody,
        id: receitaBrutaCalculadaNCM,
      };
      return putSalvarNCMReceitaBrutaCalculadaValorReferencia(requestComId);
    } else {
      return postSalvarNCMReceitaBrutaCalculadaValorReferencia(requestBody);
    }
  };

  const htmlSwitchMetodo = () => {
    const OPCOES_TIPO_METODO = [
      { value: EMetodoReceitaBrutaCalculada.PRECO_CORRENTE.toString(), label: 'Preço Corrente' },
      ...(hasFatorAjuste
        ? [{ value: EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA.toString(), label: 'Valor Referência' }]
        : []),
    ];

    let labelMetodo = '';

    if (isEdicaoNcm) {
      if (tipoMetodo === EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA) {
        labelMetodo = 'Valor Referência';
      } else {
        labelMetodo = 'Preço Corrente';
      }
    }

    return (
      <>
        <Row className={style.formRow}>
          <Col md={4} sm={12}>
            {isEdicaoNcm === true && <InformacaoBasica titulo='Método' conteudo={labelMetodo} />}
            {isEdicaoNcm === false && (
              <BrSelect
                id='select-tipo-metodo'
                label='Método'
                options={OPCOES_TIPO_METODO}
                key={tipoMetodo}
                value={tipoMetodo.toString()}
                disabled={isEdicaoNcm}
                onChange={(value: string) => setTipoMetodo(parseInt(value))}
              />
            )}
          </Col>
        </Row>

        <div className={style.formDivider}>
          <BrDivider dashed />
        </div>
      </>
    );
  };

  const validarNCMNovaEspecie = async (codigo: string) => {
    if (codigo.length !== 8) {
      setErrorCodigoNCMNovaEspecie('O código NCM deve ter 8 dígitos.');
      setIsCodigoNCMNovaEspecieValid(false);
      return;
    }
    setErrorCodigoNCMNovaEspecie('');
    setIsCodigoNCMLoading(true);

    await getCodigoNCMNovaEspecie(codigo)
      .then((result) => {
        setIsCodigoNCMLoading(false);
        setCodigoNCMNovaEspecieComDescricao(`${result.codigo} - ${result.descricao}`);
        setIsCodigoNCMNovaEspecieValid(true);
      })
      .catch((error) => {
        if (error.response?.status === 404) {
          setErrorCodigoNCMNovaEspecie('Código NCM não encontrado.');
        } else {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao consultar Código NCM da Nova Espécie. Por favor, tente novamente.',
            ...(error && { complementoSentryId: error }),
          });
        }

        setIsCodigoNCMNovaEspecieValid(false);
        setIsCodigoNCMLoading(false);
      });
  };

  const htmlFormularioBase = () => {
    return (
      <div className={style.formContainer} data-testid='form-base'>
        <Row className={style.formRow}>
          <Col md={4} sm={12}>
            <AnmSelectQuery
              id='select-ncm-bem-mineral'
              label={'Código NCM do Bem Mineral'}
              value={codigoNcmBemMineral}
              setValue={setCodigoNcmBemMineral}
              functionQuery={() => getNCMReceitaBruta()}
              keyQuery={['getNCMReceitaBruta']}
              formatResult={(result: any) =>
                result.map((ncm: any) => ({
                  label: `${ncm.codigo} - ${ncm.descricao}`,
                  value: ncm.codigo,
                }))
              }
            />
          </Col>

          <Col md={4} sm={12}>
            <AnmSelectQuery
              id='select-unidade-medida'
              label={'Unidade de Medida'}
              value={unidadeMedida}
              setValue={setUnidadeMedida}
              functionQuery={() => getUnidadesMedidaReceitaBruta()}
              keyQuery={['getUnidadesMedidaReceitaBruta']}
            />
          </Col>

          <Col md={4} sm={12}>
            <BrInput
              id='input-fase-industrializacao'
              label='Fase de Industrialização'
              autoComplete='off'
              required
              onChange={(e) => onTextAreaChange(e.target.value, faseDeIndustrializacao, setFaseDeIndustrializacao, 250)}
              value={faseDeIndustrializacao}
            />
          </Col>
        </Row>

        <Row className={style.formRowNovaEspecie}>
          <Col md={4} sm={12}>
            <BrInput
              id='input-nova-especie'
              label='Nova Espécie'
              autoComplete='off'
              required
              onChange={(e) => onTextAreaChange(e.target.value, novaEspecie, setNovaEspecie, 250)}
              value={novaEspecie}
            />
          </Col>

          <Col md={4} sm={12}>
            <label className={style.tooltipLabel} data-testid='tooltip'>
              Código NCM da Nova Espécie
              <InfoCircleTooltip
                text={'Digite apenas a parte númerica do código NCM da nova espécie'}
                id={'codigo-ncm-tooltip'}
              />
            </label>
            {isCodigoNCMLoading ? (
              <BrLoading />
            ) : (
              <>
                {!isCodigoNCMNovaEspecieValid ? (
                  <BrInput
                    id='input-codigo-ncm-nova-especie'
                    mask='########'
                    value={codigoNcmNovaEspecie}
                    onChange={(e) => {
                      setCodigoNcmNovaEspecie(e.target.value);
                    }}
                    button={
                      <Button
                        type='button'
                        icon='fas fa-check'
                        onClick={() => validarNCMNovaEspecie(codigoNcmNovaEspecie)}
                      />
                    }
                    onBlur={() => validarNCMNovaEspecie(codigoNcmNovaEspecie)}
                    status={errorCodigoNCMNovaEspecie ? 'danger' : undefined}
                    feedbackText={errorCodigoNCMNovaEspecie}
                  />
                ) : (
                  <div className={style.codigoNCMNovaEspecieContainer}>
                    <span className={style.codigoNcm}>{codigoNCMNovaEspecieComDescricao}</span>
                    <BrButton
                      type='button'
                      circle
                      size='small'
                      icon='fas fa-edit'
                      onClick={() => setIsCodigoNCMNovaEspecieValid(false)}
                    />
                  </div>
                )}
              </>
            )}
          </Col>

          <Col md={4} sm={12}>
            <AnmSelectQuery
              id='select-unidade-medida-nova-especie'
              label={'Unidade de Medida da Nova Espécie'}
              value={unidadeMedidaNovaEspecie}
              setValue={setUnidadeMedidaNovaEspecie}
              functionQuery={() => getUnidadesMedidaReceitaBruta()}
              keyQuery={['getUnidadesMedidaReceitaBrutaNovaEspecie']}
            />
          </Col>
        </Row>

        <Row className={style.formRow}>
          <Col md={4} sm={12}>
            <BrInput
              id='input-quantidade-nova-especie'
              label='Quantidade consumida na obtenção de nova espécie'
              autoComplete='off'
              numeric={{
                decimalScale: 2,
                fixedDecimalScale: false,
              }}
              onChange={(e) => setQuantidadeConsumidaNovaEspecie(e.target.value)}
              value={quantidadeConsumidaNovaEspecie}
            />
          </Col>

          <Col md={4} sm={12}>
            <BrInput
              id='input-quantidade-utilizada'
              label='Quantidade utilizada, doada ou bonificada'
              autoComplete='off'
              numeric={{
                decimalScale: 2,
                fixedDecimalScale: false,
              }}
              onChange={(e) => setQuantidadeUtilizadaDoadaOuBonificada(e.target.value)}
              value={quantidadeUtilizadaDoadaOuBonificada}
            />
          </Col>

          {tipoMetodo === EMetodoReceitaBrutaCalculada.PRECO_CORRENTE && (
            <Col md={4} sm={12}>
              <BrInput
                id='input-preco-corrente'
                label='Preço Corrente (R$)'
                autoComplete='off'
                numeric={{
                  prefix: 'R$ ',
                  thousandSeparator: '.',
                  decimalScale: 11,
                  fixedDecimalScale: false,
                }}
                onChange={(e) => setPrecoCorrente(e.target.value)}
                value={precoCorrente}
              />
            </Col>
          )}

          {tipoMetodo === EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA && (
            <Col md={4} sm={12}>
              <div className={style.tooltipLabel}>
                <div className={style.label}>
                  Valor de Produção (VP){' '}
                  <div className={style.tooltip}>
                    <InfoCircleTooltip
                      text='Informe o Valor de Produção (VP) proporcional à quantidade de bem mineral vinculada à operação que está sendo declarada, apurado nos termos do art. 2º, I, do Decreto nº 9.252/2017.'
                      id='tooltipVP'
                    />
                  </div>
                </div>
              </div>
              <BrInput
                id='input-valor-producao'
                aria-label='Valor de Produção (VP)'
                autoComplete='off'
                numeric={{
                  prefix: 'R$ ',
                  thousandSeparator: '.',
                  decimalScale: 2,
                  fixedDecimalScale: false,
                }}
                onChange={(e) => setValorProducaoTotal(e.target.value)}
                value={valorProducaoTotal}
              />
            </Col>
          )}
        </Row>
      </div>
    );
  };

  const htmlFormularioValorReferencia = () => {
    return (
      <div data-testid='form-valor-referencia'>
        <Row className={style.formRow}>
          <Col md={4} sm={12}>
            <BrInput
              id='input-teor-concentrado'
              label='% Teor Concentrado (TC)'
              autoComplete='off'
              numeric={{
                decimalScale: 2,
                fixedDecimalScale: false,
              }}
              onChange={(e) => setTeorConcentrado(e.target.value)}
              value={teorConcentrado}
            />
          </Col>

          <Col md={4} sm={12}>
            <BrInput
              id='input-teor-alimentacao'
              label='% Teor Alimentação (TA)'
              autoComplete='off'
              numeric={{
                decimalScale: 2,
                fixedDecimalScale: false,
              }}
              onChange={(e) => setTeorAlimentacao(e.target.value)}
              value={teorAlimentacao}
            />
          </Col>

          <Col md={4} sm={12}>
            {fatorDeAjusteDataLoading || (fatorDeAjusteDataFetching && <BrLoading />)}
            {fatorDeAjusteError && (
              <div className={style.errorMessage}>
                Erro ao carregar os fatores de ajuste. Por favor, tente novamente.
                <BrButton className={style.linkText} onClick={() => refetchFatorDeAjuste()} size='small'>
                  (Refazer consulta)
                </BrButton>
              </div>
            )}
            <BrSelect
              id='select-substancia-fator-ajuste'
              label={'Substância (Fatores de Ajuste)'}
              key={fatorDeAjusteData.fatoresAjuste}
              options={formatResultFatorAjuste(fatorDeAjusteData)}
              value={idSubstanciaFatorDeAjuste.toString()}
              onChange={(value: string) => setIdSubstanciaFatorDeAjuste(value)}
            />
          </Col>
        </Row>
      </div>
    );
  };

  const htmlBaseCalculo = () => {
    return (
      <div className={style.formSection}>
        <AtualizarInformacoes
          onClickFunction={() => receberInformacoesBaseCalculo()}
          info={info}
          isLoading={isLoadingInfo}
        />
      </div>
    );
  };

  const htmlSwitches = () => {
    return (
      <>
        <AnmSwitch
          label='O bem mineral é oriundo de rejeitos e estéreis de minerais associados utilizados em outras cadeias produtivas?'
          value={isRejeito}
          setValue={setIsRejeito}
          tooltipText='Informação vinculada à redução da alíquota da CFEM em 50%, conforme § 7º, do Art. 6º, da Lei nº 7.990/1989. Os procedimentos para aproveitamento de rejeitos e estéreis estão dispostos na RESOLUÇÃO ANM Nº 85, DE 2 DE DEZEMBRO DE 2021.'
        ></AnmSwitch>

        <AnmSwitch
          label='A substância possui uso específico?'
          value={hasUsoEspecifico}
          setValue={setHasUsoEspecifico}
          tooltipText='Essa marcação só deve ser feita quando o bem mineral for destinado ao uso imediato na construção civil ou se enquadrar na categoria de rochas ornamentais, para fins de aplicação da alíquota de 1% (um por cento), nos termos do ANEXO da Lei nº 8.001/1990.'
        ></AnmSwitch>
      </>
    );
  };

  const htmlLinhaBotao = () => {
    return (
      <div className={style.formButtons}>
        {isEdicaoNcm === true && (
          <BrButton
            data-testid='btn-salvar-ncm'
            secondary
            size='small'
            icon='fas fa-save'
            onClick={() => {
              salvarNCM();
            }}
            loading={loadingBotao}
          >
            Salvar NCM
          </BrButton>
        )}
        {isEdicaoNcm === false && (
          <BrButton
            data-testid='btn-incluir-ncm'
            secondary
            size='small'
            icon='fas fa-plus'
            onClick={() => {
              salvarNCM();
            }}
            loading={loadingBotao}
          >
            Incluir NCM
          </BrButton>
        )}
      </div>
    );
  };

  const htmlTributos = () => {
    if (tributosPossiveisLoading) return <BrLoading />;
    if (tributosPossiveisError) {
      return (
        <div className={style.errorContainer}>
          <span className={style.errorText}>Não foi possível atualizar a lista de tributos</span>
          <BrButton size='small' onClick={() => refetchTributosPossiveis()}>
            (Tentar novamente)
          </BrButton>
        </div>
      );
    }
    return (
      <SecaoDeducoes
        key={codigoNcmBemMineral}
        deducoesPossiveis={tributosPossiveisData}
        deducoesAdicionadas={tributosAdicionados}
        setDeducoesAdicionadas={setTributosAdicionados}
        semMargem
        isApresentarSecaoCompraTerceiros
        haCompraTerceiro={haCompraTerceiro}
        setHaCompraTerceiro={setHaCompraTerceiro}
        quantidadeCompraTerceiro={quantidadeAquisicaoTerceiros}
        setQuantidadeCompraTerceiro={setQuantidadeAquisicaoTerceiros}
        valorCompraTerceiro={valorTotalAquisicaoTerceiros}
        setValorCompraTerceiro={setValorTotalAquisicaoTerceiros}
      />
    );
  };

  const isLoading =
    receitaBrutaCalculadaMetodo === EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA
      ? isLoadingNcmAdicionadoValorReferencia
      : isLoadingNcmAdicionadoPrecoCorrente;

  return (
    <div className={style.formReceitaBrutaCalculada}>
      <div className={style.formTitle}>{isEdicaoNcm ? 'Edite NCM' : 'Adicione um NCM'}</div>
      {isLoading ? (
        <BrLoading />
      ) : (
        <>
          {htmlSwitchMetodo()}
          {htmlFormularioBase()}
          {tipoMetodo === EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA && htmlFormularioValorReferencia()}
          {htmlTributos()}
          {htmlBaseCalculo()}
          {htmlSwitches()}
          {htmlLinhaBotao()}
        </>
      )}
    </div>
  );
};

export default FormNCMReceitaBrutaCalculada;
